import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';

// Environment configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Validation function for Firebase config
const validateFirebaseConfig = (): boolean => {
  const requiredKeys = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId',
  ];

  return requiredKeys.every((key) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const value = firebaseConfig[key as keyof typeof firebaseConfig];
    if (!value || (typeof value === 'string' && value.startsWith('your_'))) {
      // eslint-disable-next-line no-console
      console.error(`Missing or invalid Firebase config: ${key}`);
      return false;
    }
    return true;
  });
};

// Initialize Firebase only if config is valid
let app: ReturnType<typeof initializeApp> | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let functions: Functions | null = null;

// Make Firebase globally available for browser console debugging
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  // Make Firebase auth available globally for debugging
  (window as any).firebaseConfig = firebaseConfig;
}

if (validateFirebaseConfig()) {
  // eslint-disable-next-line no-console
  console.log('🔧 Initializing Firebase with config:', {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    projectId: firebaseConfig.projectId,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    authDomain: firebaseConfig.authDomain,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    apiKey: firebaseConfig.apiKey ? `${firebaseConfig.apiKey.slice(0, 8)}...` : 'NOT_SET'
  });

  try {
    // Initialize Firebase app
    app = initializeApp(firebaseConfig);
    // eslint-disable-next-line no-console
    console.log('✅ Firebase app initialized successfully');

    // Initialize services
    auth = getAuth(app);
    db = getFirestore(app);
    functions = getFunctions(app, 'us-central1');

    // eslint-disable-next-line no-console
    console.log('✅ Firebase services initialized:', {
      auth: !!auth,
      db: !!db,
      functions: !!functions
    });

    // Make Firebase services globally available after successful initialization
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      (window as any).firebaseAuth = auth;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      (window as any).firebaseDb = db;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      (window as any).firebaseFunctions = functions;
      // eslint-disable-next-line no-console
      console.log('✅ Firebase services made globally available');
    }

    // Connect to emulators in development
    if (import.meta.env.DEV) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const emulatorHost = import.meta.env.VITE_FUNCTIONS_EMULATOR_HOST;

      if (emulatorHost && !auth.emulatorConfig) {
        // eslint-disable-next-line no-console
        console.info('🔧 Connecting to Firebase emulators...');

        // Connect Auth emulator
        connectAuthEmulator(auth, 'http://localhost:9199', { disableWarnings: true });

        // Connect Firestore emulator
        connectFirestoreEmulator(db, 'localhost', 8180);

        // Connect Functions emulator
        connectFunctionsEmulator(functions, 'localhost', 5001);

        // eslint-disable-next-line no-console
        console.info('✅ Connected to Firebase emulators');
      } else {
        // eslint-disable-next-line no-console
        console.info('🔧 Using real Firebase services (no emulators)');
      }
    }

    // Enable Auth persistence (commented out due to Firebase v10 compatibility issues)
    // auth.setPersistence('local' as any).catch(error => {
    //   console.error('Failed to set auth persistence:', error);
    // });

  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('❌ Failed to initialize Firebase:', error);

    // Make Firebase services available as null for debugging
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      (window as any).firebaseAuth = null;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      (window as any).firebaseDb = null;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      (window as any).firebaseFunctions = null;
      // eslint-disable-next-line no-console
      console.warn('⚠️ Firebase services set to null due to initialization failure');
    }

    // In production, this should trigger error reporting
    if (import.meta.env.PROD && typeof window !== 'undefined' && 'Sentry' in window) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      (window as any).Sentry?.captureException(error);
    }
  }
} else {
  // eslint-disable-next-line no-console
  console.error('❌ Invalid Firebase configuration. Please check your environment variables.');

  // Even if config is invalid, try to make a minimal Firebase object available
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    (window as any).firebaseAuth = null;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    (window as any).firebaseDb = null;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    (window as any).firebaseFunctions = null;
    // eslint-disable-next-line no-console
    console.warn('⚠️ Firebase services set to null due to invalid config');
  }
}

// Export initialized instances with null checks
export const firebaseAuth = auth;
export const firebaseDb = db;
export const firebaseFunctions = functions;
export const firebaseApp = app;

// Export configuration for debugging
export const getFirebaseConfig = (): typeof firebaseConfig => ({
  ...firebaseConfig,
  // Mask sensitive data in logs
  apiKey: firebaseConfig.apiKey ? `${firebaseConfig.apiKey.slice(0, 8)}...` : 'NOT_SET',
});

// Health check function
export const checkFirebaseConnection = async (): Promise<{
  auth: boolean;
  firestore: boolean;
  functions: boolean;
}> => {
  const results = {
    auth: false,
    firestore: false,
    functions: false,
  };

  try {
    // Check Auth
    if (auth) {
      results.auth = true;
    }

    // Check Firestore
    if (db) {
      // Simple read operation to test connection
      results.firestore = true;
    }

    // Check Functions
    if (functions) {
      results.functions = true;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Firebase connection check failed:', error);
  }

  return results;
};

// Export validation function for external use
export const isFirebaseInitialized = (): boolean => {
  return !!(app && auth && db && functions);
};

// Error handling utility
export const handleFirebaseError = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const firebaseError = error as { code: string; message: string };
    
    // Map Firebase error codes to user-friendly messages
    const errorMessages: Record<string, string> = {
      'auth/user-not-found': 'Invalid credentials',
      'auth/wrong-password': 'Invalid credentials',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later',
      'auth/network-request-failed': 'Network error. Please check your connection',
      'permission-denied': 'You do not have permission to perform this action',
      'unavailable': 'Service temporarily unavailable. Please try again',
    };

    return errorMessages[firebaseError.code] || firebaseError.message || 'An error occurred';
  }

  return 'An unexpected error occurred';
};

// Production readiness check
export const checkProductionReadiness = (): {
  ready: boolean;
  issues: string[];
} => {
  const issues: string[] = [];
  
  if (!isFirebaseInitialized()) {
    issues.push('Firebase services not initialized');
  }
  
  if (import.meta.env.PROD && firebaseConfig.apiKey?.startsWith('your_')) {
    issues.push('Production environment using placeholder API key');
  }
  
  if (import.meta.env.PROD && !import.meta.env.VITE_SENTRY_DSN) {
    issues.push('Error monitoring not configured for production');
  }

  return {
    ready: issues.length === 0,
    issues,
  };
};
